import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';

import { PoisonAddFormValues, poisonAddSchema } from 'api/visitReports/type';
import { MLInput } from 'components/Input/Input';
import useAddPoison from 'hooks/VisitReport/useAddPoison';

interface AddPoisonModalProps {
  slug?: string;
  id?: string;
  isOpen: boolean;
  onClose: () => void;
}

const AddPoisonModal: FC<AddPoisonModalProps> = ({ slug, id, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { createAppPoison } = useAddPoison();
  const { handleSubmit, control } = useForm<PoisonAddFormValues>({
    resolver: zodResolver(poisonAddSchema),
    defaultValues: {
      poison_name: '',
      amount_type: '',
      usage_method: '',
      usage_place: '',
      usage_reason: '',
    },
  });

  const onAddPoison = (data: PoisonAddFormValues) => {
    createAppPoison(
      { poison: data, id: id || '', slug: slug || '' },
      {
        onSuccess: () => {
          enqueueSnackbar(t('AddpoisonModal.modal-success-add'), { variant: 'success' });

          onClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>{t('AddpoisonModal.modal-title')}</ModalHeader>
      <ModalBody>
        <div className="row m-2">
          <div className="col-md-12">
            {/* Створення POST # 5 " Якщо є форма викор контроллер і міняємо name crtl + space  + створити handleSubmit " | Start Block | */}
            <Controller
              control={control}
              name="poison_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('AddpoisonModal.modal-body.name')}
                  error={error?.message}
                  required
                  {...field}
                />
              )}
            />
            {/* Створення POST # 5 " Якщо є форма викор контроллер і міняємо name crtl + space " | End Block | */}
          </div>
        </div>

        <div className="row m-2">
          <div className="col-md-8">
            <Controller
              control={control}
              name="poison_amount"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="number"
                  label={t('AddpoisonModal.modal-body.amount')}
                  error={error?.message}
                  required
                  {...field}
                  onChange={event => {
                    field.onChange(+event.target.value);
                  }}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="amount_type"
            render={({ field, fieldState: { error } }) => (
              <div className="col-md-4">
                <label className={`form-label mb-1 ${error ? 'text-danger' : ''}`}>
                  {t('AddpoisonModal.modal-body.amount-type')}
                </label>
                <select {...field} className={`form-control ${error ? 'is-invalid border-danger' : ''}`} required>
                  <option value="grams">{t('AddpoisonModal.amount-types.grams')}</option>
                  <option value="millitre">{t('AddpoisonModal.amount-types.milliliters')}</option>
                  <option value="pieces">{t('AddpoisonModal.amount-types.pieces')}</option>
                </select>
                {error && <div className="text-danger w-100 d-block mt-1">{error?.message}</div>}
              </div>
            )}
          />
        </div>
        <div className="row m-2">
          <div className="col-md-12">
            <Controller
              control={control}
              name="usage_method"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('AddpoisonModal.modal-body.usage')}
                  error={error?.message}
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="row m-2">
          <div className="col-md-12">
            <Controller
              control={control}
              name="usage_reason"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('AddpoisonModal.modal-body.reason')}
                  error={error?.message}
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="row m-2">
          <div className="col-md-12">
            <Controller
              control={control}
              name="usage_place"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('AddpoisonModal.modal-body.place')}
                  error={error?.message}
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={handleSubmit(onAddPoison)}>
          {t('AddpoisonModal.buttons.submit')}
        </Button>{' '}
        <Button
          color="danger"
          onClick={() => {
            onClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddPoisonModal;
